import { Editor, Element, Point, Text } from '../../core';
import type { Node, NodeEntry } from '../../core';
import { PARAGRAPH_LIST_ITEM_VARIANT, PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import { matchHeadingLikePrefix, normalizeHeadingLabel } from './matcher';

/**
 * RP-6004 (M1) — matcher-based section helpers that consumers use instead of the
 * heading-node lookups (`getHeadingNodeWithSynonym`, `getHeadingSectionEdgesByNode`,
 * `getHeadingNodeWithTextInRange`). Because they all route through
 * `matchHeadingLikePrefix`, what is styled as a heading and what counts as a
 * section boundary can never disagree.
 *
 * MIGRATION MAP (RP-6004 M1 -> consumers). The legacy heading-NODE helpers are
 * NOT deleted yet; each is earmarked at its definition with a matching
 * `RP-6004 (M1) DEPRECATION` comment. Consumers migrate to the matcher/paragraph
 * path behind REPORTER_HEADING_NODES_DEPRECATED, then the legacy helpers are
 * deleted with the heading plugin (RP-6012 / M9).
 *
 *   matchHeadingLikePrefix (matcher.ts)         <- getMaybeHeadingFormatted
 *   isHeadingLikeParagraph / getHeadingLikeParagraphs
 *                                               <- isHeadingNode checks, getNextHeadingOrHeadingLikeNode
 *   getHeadingLikeParagraphWithLabel (added RP-6007)
 *                                               <- getHeadingNodeWithTextInRange
 *   getSectionEdgesFromParagraphs               <- getHeadingSectionEdgesByNode / getHeadingSectionEdgesByRegex
 *   getImpressionParagraphNode                  <- getHeadingNodeWithSynonym(…, impressions)
 *
 * Consumer callsites (each migrates on its own story, all flag-gated):
 *   - hooks/useFocusMode.ts                            RP-6007 / M4  (done)
 *   - hooks/useImpressionGenerator.tsx                 RP-6008 / M5
 *   - RichTextEditor/ui/ImpressionGeneratorButton.tsx  RP-6008 / M5
 *   - RichTextEditor/plugins/deepLink/utils.ts,
 *     hooks/useDeepLinkStudy.ts                        (via getNextHeadingOrHeadingLikeNode)
 */

const leadingText = (node: Node): string => {
  if (!Element.isElement(node) || !Array.isArray(node.children)) return '';
  // Concatenate the leading run of Text nodes, stopping at the first inline/void element, so an
  // empty leading Text node (e.g. `[{text:''},{text:'Findings:'}]`) doesn't mask a real label.
  let text = '';
  for (const child of node.children) {
    if (!Text.isText(child)) break;
    text += child.text;
  }
  return text;
};

/** A top-level, non-inline, non-list-item paragraph whose leading run is a (recognised) heading label. */
export const isHeadingLikeParagraph = (node: Node, labelSet?: Set<string>): boolean => {
  if (!Element.isElement(node) || node.type !== PARAGRAPH_PLUGIN_ID) return false;
  if ((node as { shouldForceInline?: boolean }).shouldForceInline === true) return false;
  // List items are paragraphs with `variant: 'li'`; a bullet is never a section heading. Excluding
  // them here keeps every consumer (read-only renderer, duplicate-header gate, focus mode) from
  // treating a list item that happens to start with a keyword label as a heading.
  if ((node as { variant?: string }).variant === PARAGRAPH_LIST_ITEM_VARIANT) return false;
  return matchHeadingLikePrefix(leadingText(node), labelSet) != null;
};

/** All top-level heading-like paragraphs, in document order. */
export const getHeadingLikeParagraphs = (editor: Editor, labelSet?: Set<string>): NodeEntry[] => {
  const entries: NodeEntry[] = [];
  editor.children.forEach((node: Node, index: number) => {
    if (isHeadingLikeParagraph(node, labelSet)) entries.push([node, [index]]);
  });
  return entries;
};

/**
 * Section body edges for a heading-like paragraph: from just after the heading
 * paragraph to just before the next heading-like paragraph (or the document end).
 * The matcher-based replacement for `getHeadingSectionEdgesByNode`.
 */
export const getSectionEdgesFromParagraphs = (
  editor: Editor,
  headingEntry: NodeEntry,
  labelSet?: Set<string>
): { anchor: Point; focus: Point } | null => {
  const startIndex = headingEntry[1][0];
  if (typeof startIndex !== 'number') return null;

  let nextIndex = editor.children.length;
  for (let i = startIndex + 1; i < editor.children.length; i++) {
    if (isHeadingLikeParagraph(editor.children[i], labelSet)) {
      nextIndex = i;
      break;
    }
  }

  const anchor = Editor.after(editor, headingEntry[1]) ?? Editor.end(editor, headingEntry[1]);
  const focus =
    nextIndex < editor.children.length
      ? (Editor.before(editor, [nextIndex]) ?? Editor.end(editor, []))
      : Editor.end(editor, []);

  if (anchor == null || focus == null) return null;
  // A label-only section immediately followed by another heading paragraph has an empty body: its
  // `anchor` (just after the heading) sorts AFTER `focus`. Collapse the range to `anchor` so the
  // body range is empty but still starts just after the heading, rather than returning an inverted range.
  if (Point.compare(anchor, focus) > 0) return { anchor, focus: anchor };
  return { anchor, focus };
};

/**
 * The impression section's heading paragraph, matched by impression synonyms
 * (the `impressions` group of `useHeadingKeywords`). Matcher-based replacement
 * for `getHeadingNodeWithSynonym(editor, impressions, …)`.
 */
export const getImpressionParagraphNode = (
  editor: Editor,
  impressionSynonyms: Iterable<string>,
  labelSet?: Set<string>
): NodeEntry | null => {
  const synonyms = new Set(Array.from(impressionSynonyms, (s) => normalizeHeadingLabel(s)));
  for (const entry of getHeadingLikeParagraphs(editor, labelSet)) {
    const match = matchHeadingLikePrefix(leadingText(entry[0]), labelSet);
    if (match != null && synonyms.has(normalizeHeadingLabel(match.label))) return entry;
  }
  return null;
};
